import React, { createContext, type ReactNode, useCallback, useContext, useEffect } from 'react';
import { AuthContext } from './auth-context-provider';
import { type CustomerOrder } from '../entities';
import { useLocalStorage } from '../hooks/use-local-storage';
import { LocalDate } from '../utils/local-date';

interface CartContextType {
  customerOrder?: CustomerOrder;
  itemsCount: number;
  setCart: (customerOrder: CustomerOrder) => void;
  clearCart: () => void;
}

export const CartContext = createContext<CartContextType>({
  itemsCount: 0,
  setCart: () => {},
  clearCart: () => {},
});

export const CartContextProvider = ({ children }: { children: ReactNode }): ReactNode => {
  const [cartCustomerOrder, setCartCustomerOrder] =
    useLocalStorage<CustomerOrder>('cart.customerOrder');
  const [cartItemsCount, setCartItemsCount] = useLocalStorage<number>('cart.itemsCount', 0);
  const { onLogout } = useContext(AuthContext);

  const setCart = useCallback((customerOrder: CustomerOrder) => {
    setCartCustomerOrder(customerOrder);
    setCartItemsCount(
      customerOrder.siteMenuOrders.reduce(
        (acc, order) =>
          acc +
          1 +
          (order.siteMenuBeverageOrders?.length ?? 0) +
          (order.siteMenuSupplyOrders?.length ?? 0),
        0
      )
    );
  }, []);

  const clearCart = useCallback(() => {
    setCartCustomerOrder(undefined);
    setCartItemsCount(undefined);
  }, []);

  useEffect(() => {
    const logoutSubscription = onLogout.subscribe(() => {
      clearCart();
    });
    return () => {
      logoutSubscription.unsubscribe();
    };
  }, []);

  return (
    <CartContext.Provider
      value={{
        customerOrder: cartCustomerOrder
          ? Object.assign(cartCustomerOrder, {
              orderDate: new LocalDate(cartCustomerOrder?.orderDate),
            })
          : cartCustomerOrder,
        itemsCount: cartItemsCount,
        setCart,
        clearCart,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCartContext = (): CartContextType => useContext(CartContext);
